/**
 * Field and FieldGrid (R16 T-137, design v1.2 change 04 §4) — one label/value layout for records and
 * forms. Outside a FieldGrid a Field is the form cell it always was (label above the control). Inside
 * one it is a row of the grid: a 160px label in --ink-2, top-aligned, wrapping; the value in --ink.
 * The grid is two columns of pairs when its container is 720px wide or more (room for two 160px-label pairs) and one below (a container query,
 * so the docked pane and the full page lay out by the room they have). On the full record page (T-168, inside
 * [data-presenter='page']) it is three columns at a viewport of 1440+ and four at 1920+, each only when the container also has the
 * room (960px / 1280px); the pane keeps one or two. At three or four columns a row stacks (label above value), at one or two the label sits beside it; the grid reports its column count as `data-cols`. `span` takes every column (long text). In edit mode the editor sits where the value was, so nothing jumps.
 * T-171: an Input, Textarea or Select inside a Field fills its column (kit.css `.k-field` / `.k-fg-value`); a form sets no width of its own.
 */
import * as React from 'react';

const GridContext = React.createContext(false);
/** True inside a FieldGrid: an empty value reads "Not set" there, an em dash in a table cell. */
export const useInFieldGrid = () => React.useContext(GridContext);

/**
 * #87 4382914498: context crosses React portals, so a dialog opened from inside a record's grid would render its
 * Fields as grid rows (dt/dd with no dl). A dialog's content sits behind this boundary: Fields there are form cells.
 */
export function FieldGridBoundary({ children }: { children: React.ReactNode }) {
  return <GridContext.Provider value={false}>{children}</GridContext.Provider>;
}

/** `columns` (3 | 4) forces that many tracks: for the help specimen only. `data-cols` is the rendered track count; CSS stacks label above value at 3 or 4 and keeps it beside at 1 or 2. */
export function FieldGrid({ children, className, columns, ...rest }: React.HTMLAttributes<HTMLDListElement> & { columns?: 3 | 4 }) {
  const wrap = React.useRef<HTMLDivElement>(null);
  const [cols, setCols] = React.useState(1);
  React.useEffect(() => {
    const dl = wrap.current?.firstElementChild;
    if (!dl) return;
    const measure = () => setCols(getComputedStyle(dl).gridTemplateColumns.split(' ').length);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(wrap.current as Element);
    return () => ro.disconnect();
  }, []);
  return (
    <GridContext.Provider value={true}>
      <div className="k-fg-wrap" ref={wrap}>
        <dl data-kit="field-grid" data-cols={cols} data-force-cols={columns} className={className ? `k-fg ${className}` : 'k-fg'} {...rest}>
          {children}
        </dl>
      </div>
    </GridContext.Provider>
  );
}

export interface FieldProps {
  label: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  /** Inside a FieldGrid: take both columns (long text). */
  span?: boolean;
  /** Inside a FieldGrid: beside the label (a help icon). */
  labelExtra?: React.ReactNode;
  /** Inside a FieldGrid: the row's id, so "N fields need attention" can link to the first. */
  id?: string;
  'data-testid'?: string;
  children: React.ReactNode;
}

/** A label, the control, an optional hint and an error. `required` marks the label and the control. */
export function Field({ label, htmlFor, hint, error, required, span, labelExtra, id, children, ...rest }: FieldProps) {
  const inGrid = useInFieldGrid();
  const control =
    required && React.isValidElement<{ 'aria-required'?: boolean }>(children) ? React.cloneElement(children, { 'aria-required': true }) : children;
  const mark = required ? (
    <span className="k-req" aria-hidden>
      {' *'}
    </span>
  ) : null;
  const notes = (
    <>
      {hint ? <p className="k-field-hint">{hint}</p> : null}
      {error ? (
        <p className="k-field-error" role="alert">
          {error}
        </p>
      ) : null}
    </>
  );
  if (inGrid) {
    return (
      <div className="k-fg-row" data-span={span ? 'full' : undefined} data-error={error ? 'true' : undefined} id={id} data-testid={rest['data-testid']}>
        <dt className="k-fg-label">
          {htmlFor ? <label htmlFor={htmlFor}>{label}</label> : label}
          {mark}
          {labelExtra}
        </dt>
        <dd className="k-fg-value">
          {control}
          {notes}
        </dd>
      </div>
    );
  }
  return (
    <div className="k-field" id={id} data-testid={rest['data-testid']}>
      <label htmlFor={htmlFor}>
        {label}
        {mark}
      </label>
      {control}
      {notes}
    </div>
  );
}
